import { Card, Flex, Box, Text, LoadingOverlay } from '@mantine/core'
import { useCallback, useEffect, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import {
  useMutationUpdateBooking,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type ParameterID } from '@flecto/types'
import { RichTextEditor, useNotifications } from '@flecto/ui'

import classes from './styles.module.css'

export type CardBookingNotesProps = {
  bookingId?: ParameterID
}

export function CardBookingNotes({ bookingId }: CardBookingNotesProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'create-booking' })
  const { t: notificationTranslate } = useTranslation('saas', {
    keyPrefix: 'notifications',
  })

  const { showNotification } = useNotifications()

  const track = useAnalytics('segment')

  const { data: booking } = useQueryGetBookingById({ bookingId: bookingId })

  const { mutateAsync: updateBooking, isLoading } = useMutationUpdateBooking()

  const [notes, setNotes] = useState(booking?.notes ?? '')

  useEffect(() => {
    if (booking?.notes) {
      setNotes(booking?.notes)
    }
  }, [booking])

  const handleUpdateNote = useCallback(
    async (notes: string) => {
      if (booking?.notes === notes) {
        return
      }

      try {
        await updateBooking({ bookingId, booking: { notes } })

        if (booking?.internal_id) {
          showNotification({
            type: 'success',
            message: notificationTranslate('success_update_note'),
          })
        }

        if (booking?.notes === '') {
          track('BOOKING__NOTES_CREATED', {
            booking_id: booking.internal_id,
          })
        } else {
          track('BOOKING__NOTES_EDITED', {
            booking_id: Number(bookingId),
          })
        }
      } catch (error) {
        console.error('Error updating booking notes:', error)

        showNotification({
          type: 'error',
          message: notificationTranslate('error_update_note'),
        })
      }
    },
    [booking, bookingId]
  )

  return (
    <Card>
      <Box className={classes.header}>
        <Flex direction='column' gap={4}>
          <Text fw={500}>{t('notes.title')}</Text>
          <Text size='sm' c='dimmed'>
            {t('notes.description')}
          </Text>
        </Flex>
      </Box>
      <Box className={classes.textarea}>
        <LoadingOverlay visible={isLoading} />
        <RichTextEditor
          value={notes}
          placeholder={t('notes.placeholder')}
          onBlur={(notes) => handleUpdateNote(notes ?? '')}
          withToolbar={false}
          disabled={isLoading || !booking}
        />
      </Box>
    </Card>
  )
}
